/**
 * Comparativos (#557): o período contra outro, número a número.
 *
 * ── A cor diz se melhorou, e não se subiu ─────────────────────────────────
 *
 * Espera e conversas sem resposta melhoram quando caem. Pintar de verde todo
 * número que sobe faria a espera dobrar em verde. A cor sai de
 * `melhor_quando`, a seta e o sinal dizem a direção, e a PALAVRA — "melhorou",
 * "piorou" — fica à vista, para quem não vê a cor e para quem não sabe de cor
 * qual número é bom quando cai. O cartão também diz a regra ("melhor quando
 * cai").
 *
 * ── Base zero não tem porcentagem ─────────────────────────────────────────
 *
 * De 0 para 3 não é "+∞%". A tela diz "sem base para comparar".
 *
 * ── As duas barras ────────────────────────────────────────────────────────
 *
 * Agora e antes, na mesma escala (a maior das duas ocupa o trilho inteiro):
 * o tamanho da diferença se vê antes de se ler o número.
 */

import { useState, type ReactNode } from 'react';
import type { BaseDeComparacao, Comparativos, NumeroComparado } from '@cliqcrm/contracts';
import { useComparativos } from '@/shared/api/consultas';
import { Estado } from '@/shared/ui/estados/estados';
import { CabecalhoDeTela, Segmentado } from '@/shared/ui';
import {
  diaPorExtenso,
  diasDoPeriodo,
  espera,
  PERIODO_PADRAO,
  type ChaveDePeriodo,
} from '../lib/periodo';
import { BarraDoRelatorio, TituloDoCartao } from './barra';
import { ABRANGENCIA, dinheiro } from '../lib/formato';
import { BotaoExportar } from './exportar';
import { nomeDaPlanilha, planilhaDosComparativos } from '../lib/planilhas';
import './relatorios.css';

const BASES: { valor: BaseDeComparacao; rotulo: string }[] = [
  { valor: 'anterior', rotulo: 'Período anterior' },
  { valor: 'mes_anterior', rotulo: 'Mês anterior' },
  { valor: 'ano_anterior', rotulo: 'Ano anterior' },
];

export function ComparativosTela(): ReactNode {
  const [periodo, definirPeriodo] = useState<ChaveDePeriodo>(PERIODO_PADRAO);
  const [base, definirBase] = useState<BaseDeComparacao>('anterior');
  const baseValida = periodo === '90' && base === 'mes_anterior' ? 'anterior' : base;
  const consulta = useComparativos({ dias: String(diasDoPeriodo(periodo)), base: baseValida });

  return (
    <div className="relatorio">
      <CabecalhoDeTela
        titulo="Comparativos"
        apoio="Os principais números do período contra outro período."
        acoes={
          <BotaoExportar
            planilha={
              consulta.data
                ? () => ({
                    conteudo: planilhaDosComparativos(consulta.data!),
                    nome: nomeDaPlanilha('comparativos', consulta.data!.periodo),
                  })
                : null
            }
          />
        }
        {...(consulta.data
          ? {
              meta: (
                <span>
                  {diaPorExtenso(consulta.data.periodo.de)} a{' '}
                  {diaPorExtenso(consulta.data.periodo.ate)}, contra{' '}
                  {diaPorExtenso(consulta.data.base.de)} a {diaPorExtenso(consulta.data.base.ate)}
                </span>
              ),
            }
          : {})}
        extra={
          <BarraDoRelatorio periodo={periodo} aoEscolherPeriodo={definirPeriodo}>
            <span className="relatorio__conector">contra</span>
            <Segmentado<BaseDeComparacao>
              como="grupo"
              rotulo="Comparar com"
              // Mês anterior não faz sentido com 90 dias: "um mês antes" cairia
              // dentro do próprio período.
              opcoes={periodo === '90' ? BASES.filter((b) => b.valor !== 'mes_anterior') : BASES}
              escolhida={baseValida}
              aoEscolher={definirBase}
            />
          </BarraDoRelatorio>
        }
      />

      <div className="relatorio__corpo">
        <Estado consulta={consulta} forma="cartoes" linhas={3}>
          {(dados) => (
            <>
              <Grupo dados={dados} grupo="atendimento" titulo="Atendimento" />
              <Grupo dados={dados} grupo="comercial" titulo="Comercial" />
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}

function Grupo({
  dados,
  grupo,
  titulo,
}: {
  dados: Comparativos;
  grupo: NumeroComparado['grupo'];
  titulo: string;
}): ReactNode {
  const itens = dados.itens.filter((i) => i.grupo === grupo);
  const abrangencia = itens[0]?.abrangencia;
  if (itens.length === 0) return null;

  return (
    <section className="relatorio__grupo" aria-labelledby={`comparativo-${grupo}`}>
      <TituloDoCartao
        id={`comparativo-${grupo}`}
        {...(abrangencia ? { abrangencia: ABRANGENCIA[abrangencia] } : {})}
      >
        {titulo}
      </TituloDoCartao>
      <ul className="comparativos">
        {itens.map((item) => (
          <li key={item.chave} className="comparativos__item">
            <div className="comparativos__topo">
              <span className="comparativos__nome">{item.nome}</span>
              <Variacao item={item} />
            </div>
            <span className="comparativos__atual">{formatar(item, item.atual, dados.moeda)}</span>
            <Barras item={item} />
            <div className="comparativos__rodape">
              <span className="comparativos__base">
                antes: {formatar(item, item.base, dados.moeda)}
              </span>
              <span className="comparativos__regra">
                {item.melhor_quando === 'maior' ? 'melhor quando sobe' : 'melhor quando cai'}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Barras({ item }: { item: NumeroComparado }): ReactNode {
  if (item.atual === null || item.base === null) return null;
  const maior = Math.max(item.atual, item.base);
  if (maior <= 0) return null;
  return (
    <span className="comparativos__barras" aria-hidden="true">
      <span className="comparativos__trilho">
        <span
          className="comparativos__barra comparativos__barra--atual"
          style={{ width: `${(item.atual / maior) * 100}%` }}
        />
      </span>
      <span className="comparativos__trilho">
        <span
          className="comparativos__barra comparativos__barra--base"
          style={{ width: `${(item.base / maior) * 100}%` }}
        />
      </span>
    </span>
  );
}

function formatar(item: NumeroComparado, valor: number | null, moeda: string): string {
  if (valor === null) return '—';
  if (item.formato === 'dinheiro') return dinheiro(valor, moeda);
  if (item.formato === 'duracao') return espera(valor) ?? '—';
  return new Intl.NumberFormat('pt-BR').format(valor);
}

function Variacao({ item }: { item: NumeroComparado }): ReactNode {
  if (item.variacao_percentual === null) {
    return <span className="comparativos__variacao">sem base para comparar</span>;
  }
  const v = item.variacao_percentual;
  if (v === 0) return <span className="comparativos__variacao">sem mudança</span>;

  const subiu = v > 0;
  const melhorou = item.melhor_quando === 'maior' ? subiu : !subiu;
  const texto = `${subiu ? '+' : ''}${v.toLocaleString('pt-BR')}%`;
  return (
    <span
      className={`comparativos__variacao comparativos__variacao--${melhorou ? 'melhor' : 'pior'}`}
    >
      <span aria-hidden="true">{subiu ? '▲' : '▼'}</span> {texto} ·{' '}
      {melhorou ? 'melhorou' : 'piorou'}
    </span>
  );
}
